import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { apiDownloadPost, apiFetch, apiUploadFile } from "@/lib/api";
import type {
  AgganciaCommessaBody,
  CalendarioResponse,
  CaricoEliminato,
  CaricoPrevistoDetail,
  CaricoPrevistoLista,
  CaricoPrevistoNuovo,
  CommessaAnteprima,
  CommessaCandidata,
  ComposizioneCarico,
  DestinatarioAvviso,
  LinguaAvviso,
  TestoAvviso,
  TestoMailInviato,
  PartenzeGiorno,
  CaricoPrevistoPatch,
  DocumentiKpiResponse,
  DocumentiUploadResponse,
  DocCompletamento,
  DocCompletamentoBody,
  FiltroPacchetti,
  GeneratoBody,
  ManifestBody,
  PaccoDetail,
  PacchettiResponse,
  RigeneraEsito,
  RigeneraPiano,
  MailOffertaProposta,
  MerceProntaClienti,
  MerceProntaCliente,
  TemplateDetail,
  TemplatePanoramica,
  TemplateRead,
  TemplateTipo,
  ClienteTrovato,
  CommessaM3,
  ConversazioneDomanda,
  DichiaraIndirizzo,
  DomandaAida,
  DomandaArchiviata,
  EsitoDomanda,
  SpedizioniereConClienti,
  VoceRubrica,
  AnomaliaTabellone,
  DuplicatoNdp,
  Trasportatore,
  AgenteMemoria,
  AgenteWhitelistRow,
  AnnullaAnticipoBody,
  AnnullaResiduoResponse,
  ApplicaProposteBody,
  ApplicaProposteResponse,
  AssegnazioniEditBody,
  AvvisiResponse,
  AvvisoRitiroUpdate,
  ClienteEmailResponse,
  ClienteEmailUpsert,
  PropostaResolve,
  CambiaStatoCaricoBody,
  CarichiPipelineResponse,
  CaricoDettaglio,
  ChecklistDocumentiResponse,
  CommesseAperteResponse,
  ConversazioneCarico,
  CreaCaricoBody,
  LavoroResponse,
  CreaCommessaLogisticaBody,
  EmailInbound,
  FascicoloFile,
  GeneraDocumentiBody,
  ImportFoglioResponse,
  ImpostazioniGlobali,
  ImpostazioniGlobaliUpdate,
  PipelineTipo,
  Paese,
  PaeseUpsert,
  PreferenzaCliente,
  PreferenzaClienteUpsert,
  Bozza,
  ProponiCarichiResponse,
  PronteMagazzinoResponse,
  RaggruppamentoProposto,
  RigaMerceBody,
  RigheMerceResponse,
  SetDocumentiRow,
  StatoDocumenti,
  StatoLogisticoResponse,
  StatoPipeline,
  UscitePalletResponse,
} from "@/types/api";

// ── M3 v2: Pipeline logistica end-to-end ──────────────────────────

export interface UsePipelineOptions {
  stato?: StatoPipeline[];
  pipeline_tipo?: PipelineTipo;
  da_verificare?: boolean;
  discordanza_bc?: boolean;
  anticipi_in_ritardo?: boolean;
  q?: string;
}

export function useM3Pipeline(opts: UsePipelineOptions = {}) {
  const params = new URLSearchParams();
  for (const s of opts.stato ?? []) params.append("stato", s);
  if (opts.pipeline_tipo) params.set("pipeline_tipo", opts.pipeline_tipo);
  if (opts.da_verificare !== undefined) params.set("da_verificare", String(opts.da_verificare));
  if (opts.discordanza_bc !== undefined) params.set("discordanza_bc", String(opts.discordanza_bc));
  if (opts.anticipi_in_ritardo) params.set("anticipi_in_ritardo", "true");
  if (opts.q) params.set("q", opts.q);
  const qs = params.toString();
  return useQuery({
    queryKey: ["m3", "pipeline", opts],
    queryFn: () =>
      apiFetch<CarichiPipelineResponse>(`/api/v1/m3/carichi${qs ? "?" + qs : ""}`),
    staleTime: 30_000,
  });
}

/** Worklist «Lavoro» (vista primaria M3): lane aggregate per lifecycle. */
export function useLavoro() {
  return useQuery({
    queryKey: ["m3", "lavoro"],
    queryFn: () => apiFetch<LavoroResponse>("/api/v1/m3/lavoro"),
    staleTime: 30_000,
  });
}

/** Avvisi di ritiro tracciati da Aida (un record per cliente; solleciti = contatore). */
export function useAvvisi(stato?: "aperto" | "chiuso") {
  return useQuery({
    queryKey: ["m3", "avvisi", stato ?? "tutti"],
    queryFn: () =>
      apiFetch<AvvisiResponse>(`/api/v1/m3/avvisi${stato ? `?stato=${stato}` : ""}`),
    staleTime: 30_000,
  });
}

/** Aggiorna un avviso (associa cliente / chiudi / note / collega carico). */
export function useAggiornaAvviso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AvvisoRitiroUpdate }) =>
      apiFetch(`/api/v1/m3/avvisi/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "avvisi"] }),
  });
}

/** Imposta la modalità ritiro di una commessa (parcheggia / attiva / programma). */
export function useM3SetModalitaRitiro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      ordineNumero,
      modalita_ritiro,
      nota,
    }: {
      ordineNumero: number;
      modalita_ritiro: "standard" | "a_chiamata" | "programmato";
      nota?: string | null;
    }) =>
      apiFetch(`/api/v1/m3/commessa/${ordineNumero}/ritiro`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ modalita_ritiro, nota }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "lavoro"] });
      qc.invalidateQueries({ queryKey: ["m3", "bozza"] });
    },
  });
}

/** Cambio stato pipeline di un Carico (v4). */
export function useM3CambiaStatoCarico(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CambiaStatoCaricoBody) =>
      apiFetch<CaricoDettaglio>(`/api/v1/m3/carico/${caricoId}/stato`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
      qc.invalidateQueries({ queryKey: ["m3", "carico", caricoId] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/** Annulla un carico (soft) — v4. */
export function useM3AnnullaCarico(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AnnullaAnticipoBody) =>
      apiFetch<CaricoDettaglio>(`/api/v1/m3/carico/${caricoId}/annulla`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
      qc.invalidateQueries({ queryKey: ["m3", "carico", caricoId] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/** Stato logistico per N commesse: usato dalla colonna 'Logistica' in M1. */
export function useM3StatoLogisticoPerOrdini(numeri: number[]) {
  return useQuery({
    queryKey: ["m3", "stato-logistico", numeri.slice().sort()],
    queryFn: () => {
      if (numeri.length === 0) return Promise.resolve({ items: [] });
      const params = new URLSearchParams();
      for (const n of numeri) params.append("numeri", String(n));
      return apiFetch<StatoLogisticoResponse>(
        `/api/v1/m3/pipeline-per-ordini?${params.toString()}`
      );
    },
    staleTime: 15_000,
  });
}

/** Crea una commessa logistica da M1 (Passa o Anticipa). */
export function useM3CreaCommessaDaM1() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreaCommessaLogisticaBody) =>
      apiFetch<CaricoDettaglio>("/api/v1/m3/pipeline", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
      qc.invalidateQueries({ queryKey: ["m3", "stato-logistico"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/** Commesse pronte a magazzino non ancora in pipeline (tabella sopra il kanban). */
export function useM3ProneAMagazzino() {
  return useQuery({
    queryKey: ["m3", "pronte-a-magazzino"],
    queryFn: () =>
      apiFetch<PronteMagazzinoResponse>("/api/v1/m3/pronte-a-magazzino"),
    staleTime: 30_000,
  });
}

/** Checklist documenti richiesti per una commessa (calcolata da paese). */
export function useM3ChecklistDocumenti(commessaId: string | null) {
  return useQuery({
    queryKey: ["m3", "checklist-doc", commessaId],
    queryFn: () =>
      apiFetch<ChecklistDocumentiResponse>(
        `/api/v1/m3/pipeline/${commessaId}/checklist-documenti`
      ),
    enabled: !!commessaId,
    staleTime: 30_000,
  });
}

/** Commesse BC aperte (qty_residua > 0) per un cliente — usato dal dropdown
 * "Associa commessa BC" quando una commessa pipeline non ha numero_ordine. */
export function useM3CommesseAperteCliente(clienteCodice: number | null) {
  return useQuery({
    queryKey: ["m3", "commesse-aperte-cliente", clienteCodice],
    queryFn: () =>
      apiFetch<CommesseAperteResponse>(
        `/api/v1/m3/commesse-aperte-cliente/${clienteCodice}`
      ),
    enabled: clienteCodice !== null && clienteCodice > 0,
    staleTime: 60_000,
  });
}

/** CRUD Carico aggregato. */
export function useM3Carico(caricoId: string | null) {
  return useQuery({
    queryKey: ["m3", "carico", caricoId],
    queryFn: () => apiFetch<CaricoDettaglio>(`/api/v1/m3/carico/${caricoId}`),
    enabled: !!caricoId,
    staleTime: 30_000,
  });
}

export function useM3CreaCarico() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreaCaricoBody) =>
      apiFetch<CaricoDettaglio>("/api/v1/m3/carico", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3"] }),
  });
}

export function useM3ImportFoglio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<ImportFoglioResponse>("/api/v1/m3/admin/import-foglio", {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "pipeline"] }),
  });
}

// ── B4: Proposta di carico ─────────────────────────────────────────

export function useM3Proponi() {
  return useMutation({
    mutationFn: (capacita?: number) =>
      apiFetch<ProponiCarichiResponse>(
        `/api/v1/m3/carichi/proponi${capacita ? `?capacita=${capacita}` : ""}`,
        { method: "POST" }
      ),
  });
}

export function useM3Applica() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApplicaProposteBody) =>
      apiFetch<ApplicaProposteResponse>("/api/v1/m3/carichi/applica", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "pipeline"] }),
  });
}

// ── B5: Documenti ──────────────────────────────────────────────────

export function useM3DocumentiCarico(caricoId: string | null) {
  return useQuery({
    queryKey: ["m3", "documenti", caricoId],
    queryFn: () =>
      apiFetch<FascicoloFile[]>(`/api/v1/m3/carico/${caricoId}/documenti`),
    enabled: !!caricoId,
    staleTime: 30_000,
  });
}

export function useM3GeneraDocumenti(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GeneraDocumentiBody) =>
      apiFetch<FascicoloFile[]>(
        `/api/v1/m3/carico/${caricoId}/genera-documenti`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "documenti", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "checklist-doc", caricoId] });
    },
  });
}

export function useM3MarcaFirmato() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentoId, firmato }: { documentoId: string; firmato: boolean }) =>
      apiFetch<FascicoloFile>(`/api/v1/m3/documenti/${documentoId}/firma`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firmato }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "documenti"] }),
  });
}

// ── Agente Documenti («Aida») ──────────────────────────────────────

export function useM3EmailAgente(stato?: string) {
  return useQuery({
    queryKey: ["m3", "agente-emails", stato ?? "tutte"],
    queryFn: () =>
      apiFetch<EmailInbound[]>(
        `/api/v1/m3/agente/emails${stato ? `?stato=${stato}` : ""}`
      ),
    staleTime: 15_000,
  });
}

export function useM3AgganciaEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ emailId, caricoId }: { emailId: string; caricoId: string }) =>
      apiFetch<EmailInbound>(`/api/v1/m3/agente/emails/${emailId}/aggancia`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ carico_id: caricoId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "agente-emails"] });
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
    },
  });
}

export function useM3ScartaEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (emailId: string) =>
      apiFetch<EmailInbound>(`/api/v1/m3/agente/emails/${emailId}/scarta`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "agente-emails"] }),
  });
}

export function useM3CambiaStatoDocumenti(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stato: StatoDocumenti | null) =>
      apiFetch<CaricoDettaglio>(`/api/v1/m3/carico/${caricoId}/stato-documenti`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stato }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
      qc.invalidateQueries({ queryKey: ["m3", "carico", caricoId] });
    },
  });
}

export function useM3Conversazione(caricoId: string | null) {
  return useQuery({
    queryKey: ["m3", "conversazione", caricoId],
    queryFn: () =>
      apiFetch<ConversazioneCarico>(`/api/v1/m3/carico/${caricoId}/conversazione`),
    enabled: !!caricoId,
    staleTime: 15_000,
  });
}

export function useM3SetDocumenti() {
  return useQuery({
    queryKey: ["m3", "set-documenti"],
    queryFn: () => apiFetch<SetDocumentiRow[]>("/api/v1/m3/admin/set-documenti"),
    staleTime: 60_000,
  });
}

export function useM3UpsertSetDocumenti() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id?: string;
      body: Partial<SetDocumentiRow>;
    }) =>
      apiFetch<SetDocumentiRow>(
        id ? `/api/v1/m3/admin/set-documenti/${id}` : "/api/v1/m3/admin/set-documenti",
        {
          method: id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "set-documenti"] });
      qc.invalidateQueries({ queryKey: ["m3", "checklist-doc"] });
    },
  });
}

export function useM3EliminaSetDocumenti() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/m3/admin/set-documenti/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "set-documenti"] }),
  });
}

export function useM3CreaMemoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      scope: string;
      scope_ref?: string | null;
      chiave: string;
      contenuto: string;
      attiva?: boolean;
    }) =>
      apiFetch<AgenteMemoria>("/api/v1/m3/admin/agente/memorie", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "agente-memorie"] }),
  });
}

export function useM3MemorieAgente() {
  return useQuery({
    queryKey: ["m3", "agente-memorie"],
    queryFn: () => apiFetch<AgenteMemoria[]>("/api/v1/m3/admin/agente/memorie"),
    staleTime: 30_000,
  });
}

export function useM3ToggleMemoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memoriaId, attiva }: { memoriaId: string; attiva: boolean }) =>
      apiFetch<AgenteMemoria>(`/api/v1/m3/admin/agente/memorie/${memoriaId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attiva }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "agente-memorie"] }),
  });
}

export function useM3Whitelist() {
  return useQuery({
    queryKey: ["m3", "agente-whitelist"],
    queryFn: () =>
      apiFetch<AgenteWhitelistRow[]>("/api/v1/m3/admin/agente/whitelist"),
    staleTime: 60_000,
  });
}

export function useM3UpsertWhitelist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; descrizione?: string; attivo?: boolean }) =>
      apiFetch<AgenteWhitelistRow>("/api/v1/m3/admin/agente/whitelist", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "agente-whitelist"] }),
  });
}

export function useM3ImpostaAssegnazioni(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssegnazioniEditBody) =>
      apiFetch<CaricoDettaglio>(`/api/v1/m3/carico/${caricoId}/assegnazioni`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "carico", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
    },
  });
}

// ── B6: Impostazioni ───────────────────────────────────────────────

export function useM3Impostazioni() {
  return useQuery({
    queryKey: ["m3", "impostazioni"],
    queryFn: () => apiFetch<ImpostazioniGlobali>("/api/v1/m3/admin/impostazioni"),
    staleTime: 60_000,
  });
}

export function useM3AggiornaImpostazioni() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ImpostazioniGlobaliUpdate) =>
      apiFetch<ImpostazioniGlobali>("/api/v1/m3/admin/impostazioni", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "impostazioni"] }),
  });
}

export function useM3PreferenzeCliente() {
  return useQuery({
    queryKey: ["m3", "preferenze-cliente"],
    queryFn: () =>
      apiFetch<PreferenzaCliente[]>("/api/v1/m3/admin/preferenze-cliente"),
    staleTime: 60_000,
  });
}

export function useM3UpsertPreferenza() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      clienteCodice,
      body,
    }: {
      clienteCodice: number;
      body: PreferenzaClienteUpsert;
    }) =>
      apiFetch<PreferenzaCliente>(
        `/api/v1/m3/admin/preferenze-cliente/${clienteCodice}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "preferenze-cliente"] }),
  });
}

export function useM3EliminaPreferenza() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clienteCodice: number) =>
      apiFetch<void>(`/api/v1/m3/admin/preferenze-cliente/${clienteCodice}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "preferenze-cliente"] }),
  });
}

// ── Paesi → Regime (tab «Paesi & Regimi») ──────────────────────────

export function useM3Paesi() {
  return useQuery({
    queryKey: ["m3", "paesi"],
    queryFn: () => apiFetch<Paese[]>("/api/v1/m3/admin/paesi"),
    staleTime: 60_000,
  });
}

export function useM3UpsertPaese() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sigla, body }: { sigla: string; body: PaeseUpsert }) =>
      apiFetch<Paese>(`/api/v1/m3/admin/paesi/${encodeURIComponent(sigla)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "paesi"] });
      qc.invalidateQueries({ queryKey: ["m3", "proposte"] });
    },
  });
}

// ── SPEC-B: registro Marina (raggruppamenti, associazione, uscite pallet) ──

/** Proposte di raggruppamento/creazione carico da decidere (drawer «Rivedi proposte»). */
export function useM3Raggruppamenti() {
  return useQuery({
    queryKey: ["m3", "raggruppamenti"],
    queryFn: () => apiFetch<RaggruppamentoProposto[]>("/api/v1/m3/raggruppamenti"),
    staleTime: 30_000,
  });
}

export function useM3ConfermaRaggruppamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<CaricoDettaglio>(`/api/v1/m3/raggruppamenti/${id}/conferma`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "raggruppamenti"] });
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
    },
  });
}

export function useM3RifiutaRaggruppamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/v1/m3/raggruppamenti/${id}/rifiuta`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "raggruppamenti"] }),
  });
}

/** Associa un ordine BC a un carico (AssociaCommessaSection). */
export function useM3AggiungiOrdineCarico(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ordine_numero: number) =>
      apiFetch<CaricoDettaglio>(`/api/v1/m3/carico/${caricoId}/aggiungi-ordine`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ordine_numero }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
      qc.invalidateQueries({ queryKey: ["m3", "carico", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "commesse-aperte"] });
    },
  });
}

/** Report «DATI ENZO»: pallet usciti per giorno (SPEC-B2). */
export function useM3UscitePallet(dal?: string, al?: string) {
  const p = new URLSearchParams();
  if (dal) p.set("dal", dal);
  if (al) p.set("al", al);
  const qs = p.toString();
  return useQuery({
    queryKey: ["m3", "uscite-pallet", dal, al],
    queryFn: () =>
      apiFetch<UscitePalletResponse>(`/api/v1/m3/report/uscite-pallet${qs ? "?" + qs : ""}`),
    staleTime: 60_000,
  });
}

// ── SPEC-C: preparazione carichi (bozza persistita) ────────────────

const BOZZA_KEY = ["m3", "bozza"];

export function useBozza() {
  return useQuery({
    queryKey: BOZZA_KEY,
    queryFn: () => apiFetch<Bozza>("/api/v1/m3/preparazione"),
    staleTime: 10_000,
  });
}

/** Helper: hook di mutazione che POSTa e aggiorna la bozza in cache (anima la board). */
function useBozzaMut<TArg>(fn: (arg: TArg) => Promise<Bozza>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (bozza) => {
      qc.setQueryData(BOZZA_KEY, bozza);
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
    },
  });
}

export function useProponiBozza() {
  return useBozzaMut<{ finestra?: number; parziali?: boolean } | void>((opts) => {
    const p = new URLSearchParams();
    if (opts && opts.finestra) p.set("finestra", String(opts.finestra));
    if (opts && opts.parziali === false) p.set("parziali", "false");
    const qs = p.toString();
    return apiFetch<Bozza>(`/api/v1/m3/preparazione/proponi${qs ? "?" + qs : ""}`, {
      method: "POST",
    });
  });
}

export function useSpostaPallet() {
  return useBozzaMut<{ da_carico: string; a_carico: string; articolo: string; qty_pallet: number }>(
    (b) =>
      apiFetch<Bozza>("/api/v1/m3/preparazione/sposta", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(b),
      }),
  );
}

export function useTogliPallet() {
  return useBozzaMut<{ carico: string; articolo: string; qty_pallet: number }>((b) =>
    apiFetch<Bozza>("/api/v1/m3/preparazione/togli", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(b),
    }),
  );
}

export function useAggiungiPallet() {
  return useBozzaMut<{ carico: string; articolo: string; qty_pallet: number }>((b) =>
    apiFetch<Bozza>("/api/v1/m3/preparazione/aggiungi", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(b),
    }),
  );
}

/** Annulla un residuo dalla proposta (decisione di Marina; annullamento vero su
 * Cube → promemoria). Il `conferma_carico` di SPEC-C è rimosso: il carico reale
 * nasce dalla NDP via email (spec M3 2026-06-19). */
export function useAnnullaResiduoBozza() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<AnnullaResiduoResponse>(
        `/api/v1/m3/preparazione/carico/${id}/annulla-residuo`,
        { method: "POST" },
      ),
    onSuccess: (res) => {
      qc.setQueryData(BOZZA_KEY, res.bozza);
      qc.invalidateQueries({ queryKey: ["m3", "lavoro"] });
    },
  });
}

/** Parcheggia una COMMESSA (ordine): ritiro a chiamata permanente, esce dalla
 * proposta (intera, anche se su più camion). Lo sblocca solo una NDP. Pulsante
 * primario in UI. Aggiorna bozza + worklist + dashboard. */
export function useParcheggiaCommessaBozza() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ordine: number) =>
      apiFetch<Bozza>(`/api/v1/m3/preparazione/commessa/${ordine}/parcheggia`, { method: "POST" }),
    onSuccess: (bozza) => {
      qc.setQueryData(BOZZA_KEY, bozza);
      qc.invalidateQueries({ queryKey: ["m3", "lavoro"] });
      qc.invalidateQueries({ queryKey: ["m3", "dashboard-logistica"] });
    },
  });
}

/** Invia la proposta al cliente: promuove la bozza-carico a Carico reale PROPOSTO
 * (impegna la merce, esce dalla proposta, entra nel Lavoro). Endpoint v5. */
export function useInviaProposta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (propostaCaricoId: string) =>
      apiFetch<Bozza>(`/api/v1/m3/preparazione/carico/${propostaCaricoId}/promuovi`, {
        method: "POST",
      }),
    onSuccess: (bozza) => {
      qc.setQueryData(BOZZA_KEY, bozza);
      qc.invalidateQueries({ queryKey: ["m3", "lavoro"] });
      qc.invalidateQueries({ queryKey: ["m3", "carichi"] });
      qc.invalidateQueries({ queryKey: ["m3", "dashboard-logistica"] });
    },
  });
}

/** Replace delle assegnazioni di un carico-bozza (drag/stepper manuale persistito). */
export function useImpostaAssegnazioniBozza() {
  return useBozzaMut<{ caricoId: string; assegnazioni: unknown[] }>((b) =>
    apiFetch<Bozza>(`/api/v1/m3/preparazione/carico/${b.caricoId}/assegnazioni`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ assegnazioni: b.assegnazioni }),
    }),
  );
}

// ── Sezione Documenti (redesign M3 #29) ──────────────────────────────

const DOCUMENTI_KEY = ["m3", "documenti-pacchetti"];

/** Storico dei pacchetti documenti (filtro stato + testuale opzionale). */
export function useM3DocumentiPacchetti(
  filtro: FiltroPacchetti = "aperti",
  q?: string,
  pagina = 1,
) {
  const qs = new URLSearchParams({ stato: filtro, pagina: String(pagina) });
  if (q) qs.set("q", q);
  return useQuery({
    queryKey: [...DOCUMENTI_KEY, filtro, q ?? "", pagina],
    queryFn: () =>
      apiFetch<PacchettiResponse>(`/api/v1/m3/documenti/pacchetti?${qs.toString()}`),
    staleTime: 15_000,
    // La pagina precedente resta a schermo mentre arriva la nuova: senza, ogni
    // clic su «avanti» fa lampeggiare la tabella vuota — e la risposta, che
    // ricalcola lo stato di tutti i pacchetti, non è istantanea.
    placeholderData: (precedente) => precedente,
  });
}

/** Resoconto di un singolo pacchetto. */
export function useM3DocumentiPacchetto(caricoId: string | null) {
  return useQuery({
    queryKey: ["m3", "documenti-pacchetto", caricoId],
    queryFn: () => apiFetch<PaccoDetail>(`/api/v1/m3/documenti/pacchetti/${caricoId}`),
    enabled: !!caricoId,
    staleTime: 15_000,
  });
}

/** KPI della sezione Documenti. */
export function useM3DocumentiKpi() {
  return useQuery({
    queryKey: ["m3", "documenti-kpi"],
    queryFn: () => apiFetch<DocumentiKpiResponse>("/api/v1/m3/documenti/kpi"),
    staleTime: 30_000,
  });
}

/** Upload di una NDP/DDT nel canale «trascinamento» → pipeline agente. */
export function useM3DocumentiUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, caricoId }: { file: File; caricoId?: string }) => {
      const fd = new FormData();
      fd.append("file", file);
      if (caricoId) fd.append("carico_id", caricoId);
      return apiUploadFile<DocumentiUploadResponse>("/api/v1/m3/documenti/upload", fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-kpi"] });
      // Una NDP chiude la riga del tabellone e un DDT la fa uscire: il documento
      // caricato qui cambia i carichi previsti, non solo la sezione Documenti.
      qc.invalidateQueries({ queryKey: ["m3", "carichi-previsti"] });
    },
  });
}

/** Campi completabili di un documento (generico per tipo). */
export function useM3DocCompletamento(caricoId: string | null, tipo: string | null) {
  return useQuery({
    queryKey: ["m3", "completamento", caricoId, tipo],
    queryFn: () =>
      apiFetch<DocCompletamento>(
        `/api/v1/m3/documenti/pacchetti/${caricoId}/completamento/${tipo}`,
      ),
    enabled: !!caricoId && !!tipo,
    staleTime: 5_000,
  });
}

/** Salva i completamenti manuali e rigenera il documento. */
export function useM3SalvaDocCompletamento(caricoId: string, tipo: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DocCompletamentoBody) =>
      apiFetch<DocCompletamento>(
        `/api/v1/m3/documenti/pacchetti/${caricoId}/completamento/${tipo}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "completamento", caricoId, tipo] });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
    },
  });
}

/** Imposta (o cancella) la data di spedizione e rigenera il pacchetto. */
export function useM3ImpostaDataDocumenti(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: string | null) =>
      apiFetch<PaccoDetail>(`/api/v1/m3/documenti/pacchetti/${caricoId}/data-documenti`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "righe-merce", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "completamento", caricoId] });
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
      // La data diventa anche la data di ritiro della riga del tabellone.
      qc.invalidateQueries({ queryKey: ["m3", "carichi-previsti"] });
    },
  });
}

/** Righe merce del carico: l'elenco che alimenta i documenti. */
export function useM3RigheMerce(caricoId: string | null) {
  return useQuery({
    queryKey: ["m3", "righe-merce", caricoId],
    queryFn: () =>
      apiFetch<RigheMerceResponse>(`/api/v1/m3/documenti/pacchetti/${caricoId}/righe`),
    enabled: !!caricoId,
    staleTime: 5_000,
  });
}

/** Rilegge il PDF della nota col parser e rifà le righe (perde le correzioni). */
export function useM3RileggiRigheMerce(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<RigheMerceResponse>(
        `/api/v1/m3/documenti/pacchetti/${caricoId}/righe/rileggi`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "righe-merce", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
    },
  });
}

/** Salva le righe corrette: ratifica + rigenerazione dell'intero pacchetto. */
export function useM3SalvaRigheMerce(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (righe: RigaMerceBody[]) =>
      apiFetch<RigheMerceResponse>(`/api/v1/m3/documenti/pacchetti/${caricoId}/righe`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ righe }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "righe-merce", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
    },
  });
}

/** Disambiguazione: assegna un file caricato (in coda) a un pacchetto. */
export function useM3AssegnaUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, caricoId }: { fileId: string; caricoId: string }) =>
      apiFetch<PaccoDetail>(`/api/v1/m3/documenti/upload/${fileId}/assegna`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pacchetto_id: caricoId }),
      }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", p.carico_id] });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-kpi"] });
    },
  });
}

/** Rigenera l'intero pacchetto (mantieni override coerenti | riparti da zero). */
export function useM3RigeneraPacchetto(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { modalita: "mantieni" | "zero" }) =>
      apiFetch<RigeneraEsito>(`/api/v1/m3/documenti/pacchetti/${caricoId}/rigenera`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "completamento", caricoId] });
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
    },
  });
}

export type StatoDocRigenerazione = "attesa" | "in_corso" | "fatto" | "non_uscito";

export interface Rigenerazione {
  fase: "ferma" | "prepara" | "genera" | "fatta" | "bloccata" | "errore";
  documenti: { tipo: string; label: string; stato: StatoDocRigenerazione }[];
  messaggio: string;
}

const RIGENERAZIONE_FERMA: Rigenerazione = { fase: "ferma", documenti: [], messaggio: "" };

/**
 * Rigenerazione del pacchetto con avanzamento vero (03-10-2026).
 *
 * Il pacchetto intero in una chiamata dura 20-40 s (ogni documento passa da
 * LibreOffice) e intanto non può dire niente. Qui si chiede prima l'elenco
 * (`rigenera/prepara`: override + cancelli), poi si genera un documento alla
 * volta: ogni risposta fa avanzare la barra e spunta il documento.
 */
export function useM3RigenerazioneConAvanzamento(caricoId: string) {
  const qc = useQueryClient();
  const [stato, setStato] = useState<Rigenerazione>(RIGENERAZIONE_FERMA);

  const avvia = useCallback(
    async (modalita: "mantieni" | "zero") => {
      const base = `/api/v1/m3/documenti/pacchetti/${caricoId}`;
      setStato({ fase: "prepara", documenti: [], messaggio: "" });
      let finale: Rigenerazione = RIGENERAZIONE_FERMA;
      try {
        const piano = await apiFetch<RigeneraPiano>(`${base}/rigenera/prepara`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ modalita }),
        });
        if (piano.bloccato) {
          finale = { fase: "bloccata", documenti: [], messaggio: piano.messaggio };
          return finale;
        }
        const documenti = piano.documenti.map((d) => ({
          ...d,
          stato: "attesa" as StatoDocRigenerazione,
        }));
        const segna = (i: number, s: StatoDocRigenerazione) => {
          documenti[i] = { ...documenti[i], stato: s };
          setStato({ fase: "genera", documenti: [...documenti], messaggio: "" });
        };
        for (let i = 0; i < documenti.length; i++) {
          segna(i, "in_corso");
          // `forza`: gli override incoerenti li ha già scartati la preparazione.
          const esito = await apiFetch<RigeneraEsito>(
            `${base}/documenti/${documenti[i].tipo}/rigenera?forza=true`,
            { method: "POST" }
          );
          segna(i, esito.documenti_generati ? "fatto" : "non_uscito");
        }
        finale = { fase: "fatta", documenti: [...documenti], messaggio: "" };
        return finale;
      } catch (err) {
        finale = {
          fase: "errore",
          documenti: [],
          messaggio: err instanceof Error ? err.message : "Rigenerazione interrotta.",
        };
        return finale;
      } finally {
        setStato((s) =>
          finale.fase === "errore" ? { ...finale, documenti: s.documenti } : finale
        );
        qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
        qc.invalidateQueries({ queryKey: ["m3", "completamento", caricoId] });
        qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
      }
    },
    [caricoId, qc]
  );

  return { stato, avvia };
}

/** Rigenera un singolo documento (mantieni-by-default; 409 su conflitto → forza). */
export function useM3RigeneraDocumento(caricoId: string, tipo: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ forza }: { forza?: boolean } = {}) =>
      apiFetch<RigeneraEsito>(
        `/api/v1/m3/documenti/pacchetti/${caricoId}/documenti/${tipo}/rigenera${forza ? "?forza=true" : ""}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "completamento", caricoId, tipo] });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
    },
  });
}

/** Salva un override manuale a livello di pacchetto (chiave condivisa) + ricalcola. */
export function useM3SalvaOverride(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { chiave_campo: string; contesto?: string | null; valore: string }) =>
      apiFetch<PaccoDetail>(`/api/v1/m3/documenti/pacchetti/${caricoId}/override`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "completamento", caricoId] });
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
    },
  });
}

/** Elimina un pacchetto (Carico + fascicolo, cascade). Irreversibile. */
export function useM3EliminaPacchetto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (caricoId: string) =>
      apiFetch<void>(`/api/v1/m3/documenti/pacchetti/${caricoId}`, { method: "DELETE" }),
    onSuccess: (_d, caricoId) => {
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-kpi"] });
    },
  });
}

// ── Motore documenti: sezione Template in Impostazioni (SPEC v1) ─────

const TEMPLATE_KEY = ["m3", "template"] as const;

/** Tipi documento configurabili come template (plico generabile). */
export function useM3TemplateTipi() {
  return useQuery({
    queryKey: [...TEMPLATE_KEY, "tipi"],
    queryFn: () => apiFetch<TemplateTipo[]>("/api/v1/m3/template/tipi"),
    staleTime: 5 * 60_000,
  });
}

/** Elenco template (attivi per default; opzionalmente filtrati per paese). */
export function useM3Template(paese?: string | null, includiStorico = false) {
  const qs = new URLSearchParams();
  if (paese) qs.set("paese", paese);
  if (includiStorico) qs.set("includi_storico", "true");
  return useQuery({
    queryKey: [...TEMPLATE_KEY, "list", paese ?? null, includiStorico],
    queryFn: () =>
      apiFetch<TemplateRead[]>(`/api/v1/m3/template${qs.toString() ? `?${qs}` : ""}`),
    staleTime: 10_000,
  });
}

/** Dettaglio di un template (manifest + placeholder + diff). */
export function useM3TemplateDettaglio(id: string | null) {
  return useQuery({
    queryKey: [...TEMPLATE_KEY, "detail", id],
    queryFn: () => apiFetch<TemplateDetail>(`/api/v1/m3/template/${id}`),
    enabled: !!id,
    staleTime: 5_000,
  });
}

/** Storico versioni per (paese, tipo). */
export function useM3TemplateVersioni(tipo: string | null, paese?: string | null) {
  const qs = new URLSearchParams();
  if (tipo) qs.set("tipo", tipo);
  if (paese) qs.set("paese", paese);
  return useQuery({
    queryKey: [...TEMPLATE_KEY, "versioni", tipo, paese ?? null],
    queryFn: () => apiFetch<TemplateRead[]>(`/api/v1/m3/template/versioni?${qs}`),
    enabled: !!tipo,
    staleTime: 5_000,
  });
}

function invalidaTemplate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: TEMPLATE_KEY });
  // Cambiare i template cambia lo stato «manca template» dei pacchetti.
  qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
  qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto"] });
}

/** Panoramica a slot dei template per un regime (lista attesa × caricati). */
export function useM3TemplatePanoramica(regime: string | null) {
  return useQuery({
    queryKey: [...TEMPLATE_KEY, "panoramica", regime],
    queryFn: () =>
      apiFetch<TemplatePanoramica>(
        `/api/v1/m3/template/panoramica?regime=${encodeURIComponent(regime ?? "")}`,
      ),
    enabled: !!regime,
    staleTime: 10_000,
  });
}

/** Carica/sostituisce il .docx di un template_word (nuova versione). Scope:
 * paese_iso (override) oppure regime (default del regime). */
export function useM3TemplateUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      file: File;
      tipo_documento: string;
      paese_iso?: string | null;
      regime?: string | null;
      descrizione?: string | null;
      note_versione?: string | null;
    }) => {
      const fd = new FormData();
      fd.append("file", args.file);
      fd.append("tipo_documento", args.tipo_documento);
      if (args.paese_iso) fd.append("paese_iso", args.paese_iso);
      if (args.regime) fd.append("regime", args.regime);
      if (args.descrizione) fd.append("descrizione", args.descrizione);
      if (args.note_versione) fd.append("note_versione", args.note_versione);
      return apiUploadFile<TemplateDetail>("/api/v1/m3/template/upload", fd);
    },
    onSuccess: () => invalidaTemplate(qc),
  });
}

/** Crea/aggiorna (nuova versione) un documento `generato`. */
export function useM3TemplateGenerato() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GeneratoBody) =>
      apiFetch<TemplateDetail>("/api/v1/m3/template/generato", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaTemplate(qc),
  });
}

/** Salva il manifest (nuova versione, asset portato avanti). */
export function useM3TemplateManifest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ManifestBody) =>
      apiFetch<TemplateDetail>(`/api/v1/m3/template/${id}/manifest`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaTemplate(qc),
  });
}

/** Riattiva una versione storica. */
export function useM3TemplateAttiva() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<TemplateDetail>(`/api/v1/m3/template/${id}/attiva`, { method: "POST" }),
    onSuccess: () => invalidaTemplate(qc),
  });
}

// ── M3 «Carichi previsti» (fase pre-NDP) ────────────────────────────

const CP_KEY = ["m3", "carichi-previsti"] as const;

/** Lista carichi previsti + i conteggi della testata.
 *
 *  `stato` sono i cinque riquadri della testata; `tutti` non è un riquadro — è
 *  quello che usa la ricerca, perché include anche le righe già uscite. */
export function useCarichiPrevisti(
  stato:
    | "attivi"
    | "da_sollecitare"
    | "con_domande"
    | "da_confermare_uscita"
    | "usciti"
    | "tutti" = "attivi",
) {
  return useQuery({
    queryKey: [...CP_KEY, "list", stato],
    queryFn: () =>
      apiFetch<CaricoPrevistoLista>(`/api/v1/m3/carichi-previsti?stato=${stato}`),
    staleTime: 10_000,
  });
}

/** Dettaglio di un carico previsto (thread, composizione, mapping). */
export function useCaricoPrevisto(id: string | null) {
  return useQuery({
    queryKey: [...CP_KEY, "detail", id],
    queryFn: () => apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}`),
    enabled: !!id,
    staleTime: 5_000,
  });
}

function invalidaCp(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: CP_KEY });
}

/** Nota + conferma lettura Aida. */
/** Le righe delle commesse scelte nel «+ Nuovo carico», con la disponibilità. */
export function useCommesseRighe(numeri: number[]) {
  const chiave = [...numeri].sort((a, b) => a - b);
  return useQuery({
    queryKey: ["m3", "commesse-righe", chiave],
    queryFn: () =>
      apiFetch<{ commesse: CommessaAnteprima[]; soglia_mezzo_pallet_pct: number }>(
        `/api/v1/m3/commesse-righe?${chiave.map((n) => `numeri=${n}`).join("&")}`,
      ),
    enabled: chiave.length > 0,
    staleTime: 30_000,
  });
}

/** I parametri con cui si propone il testo: `dataOrdine` undefined = quella
 *  dell'export, "" = toglierla, ISO = quella. */
export interface ParametriMail {
  lingua: LinguaAvviso;
  destinatario: DestinatarioAvviso;
  dataOrdine?: string;
}

/** Il testo della mail di avviso di una riga, nella lingua e per chi si vuole. */
export function useTestoAvviso(id: string | null, p: ParametriMail) {
  const q = new URLSearchParams({ lingua: p.lingua, destinatario: p.destinatario });
  if (p.dataOrdine !== undefined) q.set("data_ordine", p.dataOrdine);
  return useQuery({
    queryKey: [...CP_KEY, "testo-avviso", id, p.lingua, p.destinatario, p.dataOrdine ?? null],
    queryFn: () =>
      apiFetch<TestoAvviso>(`/api/v1/m3/carichi-previsti/${id}/testo-avviso?${q.toString()}`),
    enabled: !!id,
  });
}

/** Il testo della mail per un carico che non esiste ancora (passo 3 del dialogo):
 *  il server lo crea in un savepoint, genera il testo e annulla. */
export function useAnteprimaTesto(carico: CaricoPrevistoNuovo | null, p: ParametriMail) {
  return useQuery({
    queryKey: ["m3", "anteprima-testo", carico, p.lingua, p.destinatario, p.dataOrdine ?? null],
    queryFn: () =>
      apiFetch<TestoAvviso>("/api/v1/m3/nuovo-carico/anteprima-testo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          carico,
          lingua: p.lingua,
          destinatario: p.destinatario,
          data_ordine: p.dataOrdine ? p.dataOrdine : null,
          data_ordine_export: p.dataOrdine === undefined,
        }),
      }),
    enabled: !!carico,
  });
}

/** Il corpo da mandare al server insieme al testo come l'ha lasciato Marina. */
export function testoInviato(corpo: string, p: ParametriMail): TestoMailInviato {
  return {
    corpo,
    lingua: p.lingua,
    destinatario: p.destinatario,
    data_ordine: p.dataOrdine ? p.dataOrdine : null,
    data_ordine_export: p.dataOrdine === undefined,
  };
}

/** Marina ha copiato il testo: la riga aspetta la mail («non ancora vista»), e
 *  le sue aggiunte vanno nelle Note. */
export function useCpAvvisoPreparato(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (testo?: TestoMailInviato) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}/avviso-preparato`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(testo ?? null),
      }),
    onSuccess: () => invalidaCp(qc),
  });
}

/** Elimina (archivia) un carico creato per errore. */
export function useCpElimina(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nota?: string) =>
      apiFetch<CaricoEliminato>(`/api/v1/m3/carichi-previsti/${id}/elimina`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nota: nota?.trim() || null }),
      }),
    onSuccess: () => {
      invalidaCp(qc);
      invalidaDomande(qc);
    },
  });
}

/** L'«Annulla» di «Elimina carico». */
export function useCpRipristina() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (e: Pick<CaricoEliminato, "id" | "domande_chiuse">) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${e.id}/ripristina`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domande: e.domande_chiuse }),
      }),
    onSuccess: () => {
      invalidaCp(qc);
      invalidaDomande(qc);
    },
  });
}

export function useCpPatch(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CaricoPrevistoPatch) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    // un campo compilato può chiudere la domanda di Aida su quella riga
    onSuccess: () => invalidaDomande(qc),
  });
}

/** Cosa c'è sul carico: commesse, righe e disponibilità viva da magazzino. */
export function useCpComposizione(id: string | null) {
  return useQuery({
    queryKey: [...CP_KEY, "composizione", id],
    queryFn: () =>
      apiFetch<ComposizioneCarico>(`/api/v1/m3/carichi-previsti/${id}/composizione`),
    enabled: !!id,
    // La disponibilità cambia quando entra una giacenza: breve, ma non zero —
    // l'allocazione globale costa 0.3s e non va rifatta a ogni render.
    staleTime: 15_000,
  });
}

/** Le commesse agganciabili a questo carico (ricerca per numero o cliente). */
export function useCpCommesseCandidate(id: string | null, q: string) {
  return useQuery({
    queryKey: [...CP_KEY, "candidate", id, q],
    queryFn: () =>
      apiFetch<{ items: CommessaCandidata[] }>(
        `/api/v1/m3/carichi-previsti/${id}/commesse-candidate${q ? `?q=${encodeURIComponent(q)}` : ""}`,
      ),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/**
 * La stessa ricerca, ma per un carico che **non esiste ancora** («+ Nuovo
 * carico»): una casella sola, numero o cliente.
 *
 * Sta su M3 e non su `useM1Ordini` perché quello è dietro `modulo.M1`, che
 * Marina non ha: cercava e prendeva 403 a ogni battuta.
 */
export function useCommesseCandidate(q: string, enabled: boolean) {
  return useQuery({
    queryKey: [...CP_KEY, "candidate-nuovo", q],
    queryFn: () =>
      apiFetch<{ items: CommessaCandidata[] }>(
        `/api/v1/m3/commesse-candidate?q=${encodeURIComponent(q)}`,
      ),
    enabled,
    staleTime: 30_000,
  });
}

function invalidaComposizione(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: CP_KEY });
}

/**
 * Marina corregge **a quali commesse** è imputato il carico.
 *
 * Diverso dall'aggancio: quello compone il carico e la NDP lo congela. Questo
 * dice *di chi è la merce*, ed è la frase che M1 mostra nel Cruscotto — quindi
 * si può correggere anche a NDP emessa. Serve ai 7 carichi che in fascicolo
 * hanno una packing list al posto della nota, dove il parser si astiene e
 * nessun automatismo può decidere.
 */
export function useCpCorreggiAttribuzione(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { commesse: number[]; nota?: string }) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}/attribuzione`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaComposizione(qc),
  });
}

/**
 * Crea una riga di tabellone con **solo le righe d'ordine scelte**.
 *
 * È il ponte che mancava fra «cosa è pronto» e «cosa carico»: un camion si fa
 * con articoli di più commesse, e una commessa parzialmente pronta può partire
 * lo stesso. Le commesse le ricava il server dalle righe.
 */
export function useM3CreaCaricoDaRighe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { righe_id: string[]; note?: string }) =>
      apiFetch<CaricoPrevistoDetail>("/api/v1/m3/carichi-previsti/da-righe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CP_KEY });
      qc.invalidateQueries({ queryKey: ["m1"] });
    },
  });
}

/** Aggancia una commessa **intera** al carico (la selezione in blocco). Con
 *  `riferimento`: il riferimento citato dal cliente impara la mappa. */
export function useCpAggiungiCommessa(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (arg: number | { ordine_numero: number; riferimento?: string | null }) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}/commesse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(typeof arg === "number" ? { ordine_numero: arg } : arg),
      }),
    onSuccess: () => {
      invalidaComposizione(qc);
      invalidaDomande(qc);
    },
  });
}

/** Il cliente della riga, scelto dall'anagrafica (30-09-2026). */
export function useCpCliente(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cliente_codice: number) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}/cliente`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cliente_codice }),
      }),
    onSuccess: () => {
      invalidaCp(qc);
      invalidaDomande(qc);
    },
  });
}

export function useCpTogliCommessa(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ordine_numero: number) =>
      apiFetch<CaricoPrevistoDetail>(
        `/api/v1/m3/carichi-previsti/${id}/commesse/${ordine_numero}`,
        { method: "DELETE" },
      ),
    onSuccess: () => invalidaDomande(qc),
  });
}

/** Include, esclude o riduce righe — più righe in una chiamata, così «tutta la
 *  commessa» è un gesto e non venti richieste. */
export function useCpScelteComposizione(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (righe: { riga_id: string; escluso?: boolean; colli?: number }[]) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}/composizione`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ righe }),
      }),
    onSuccess: () => invalidaComposizione(qc),
  });
}

/** Aggiunge una riga al tabellone a mano (pulsante «+ Nuovo carico»).
 *
 * Fino al 27-08-2026 le righe nascevano solo da un'email letta da Aida o dalla
 * NDP: tutto ciò che il foglio di Marina esprime e un'email no — i carichi senza
 * numero d'ordine, gli stralci — non era scrivibile da nessuna parte. */
export function useCpCrea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CaricoPrevistoNuovo) =>
      apiFetch<CaricoPrevistoDetail>("/api/v1/m3/carichi-previsti", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaCp(qc),
  });
}

/**
 * Le stesse due mutazioni, ma con l'id passato alla chiamata invece che al
 * montaggio: nel calendario la riga su cui si scrive la decide il trascinamento,
 * quindi non c'è un id noto quando si prepara la mutazione.
 */
export function useCpPatchRiga() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CaricoPrevistoPatch }) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaCp(qc),
  });
}

export function useCpUscitaRiga() {
  const qc = useQueryClient();
  return useMutation({
    // `giorno` assente = uscita di oggi (dalla riga del tabellone); con la data
    // = «è uscito quel giorno», il gesto del calendario sul passato.
    mutationFn: ({ id, giorno }: { id: string; giorno?: string | null }) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}/partenza`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uscito: true, ...(giorno ? { giorno } : {}) }),
      }),
    onSuccess: () => invalidaCp(qc),
  });
}

/** Segna il giorno di partenza, lo revoca (giorno=null) o conferma l'uscita. */
export function useCpPartenza(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { giorno?: string | null; uscito?: boolean }) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}/partenza`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaCp(qc),
  });
}

const DOMANDE_KEY = ["m3", "domande"] as const;

/** Le domande aperte di Aida che riguardano Marina: solo quelle su una riga viva
 *  del tabellone, cioè quelle col badge «?» (senza i «dati plico», che sono
 *  lavoro operativo e vivono sul carico). */
export function useDomandeAida() {
  return useQuery({
    queryKey: DOMANDE_KEY,
    queryFn: () => apiFetch<DomandaAida[]>("/api/v1/m3/domande"),
  });
}

/** Le email che Aida non ha saputo agganciare a nessuna riga: sezione a parte,
 *  fuori dal conteggio delle domande (Enrico, 23-09). Sotto `DOMANDE_KEY`, così
 *  ogni gesto sulle domande la ricarica insieme alle altre. */
export function useEmailDaSmistare() {
  return useQuery({
    queryKey: [...DOMANDE_KEY, "da-smistare"],
    queryFn: () => apiFetch<DomandaAida[]>("/api/v1/m3/domande/da-smistare"),
  });
}

/** Una domanda che si chiude spegne il badge «?» sulla sua riga: si ricaricano
 *  entrambe le cose, o il tabellone mostrerebbe un badge che la pagina non ha più. */
function invalidaDomande(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: DOMANDE_KEY });
  invalidaCp(qc);
}

const JSON_POST = (body: unknown) => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

/** Le domande sospese («non lo so ancora»): fuori dal conto, non sparite. */
export function useDomandeSospese() {
  return useQuery({
    queryKey: [...DOMANDE_KEY, "sospese"],
    queryFn: () => apiFetch<DomandaAida[]>("/api/v1/m3/domande/sospese"),
  });
}

/** La risposta a parole: stesso percorso dell'email, **effetti compresi**
 *  (30-09-2026) — l'email si aggancia davvero, i valori vanno sulla riga. */
export function useRispondiDomanda(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { testo: string }) =>
      apiFetch<EsitoDomanda>(`/api/v1/m3/domande/${id}/rispondi`, JSON_POST(body)),
    onSuccess: () => invalidaDomande(qc),
  });
}

/** Le tre uscite sempre presenti: nessuna di queste · non esiste · non lo so ancora. */
export function useUscitaDomanda(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { uscita: "nessuna" | "non_esiste" | "non_lo_so"; nota?: string | null }) =>
      apiFetch<EsitoDomanda>(`/api/v1/m3/domande/${id}/uscita`, JSON_POST(body)),
    onSuccess: () => invalidaDomande(qc),
  });
}

/** Annulla l'ultima risposta: effetti all'indietro, domanda di nuovo aperta. */
export function useAnnullaRisposta(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tentativoId: string) =>
      apiFetch<{ ok: boolean }>(`/api/v1/m3/domande/${id}/tentativi/${tentativoId}/annulla`, {
        method: "POST",
      }),
    onSuccess: () => invalidaDomande(qc),
  });
}

/** «Aggiungi alle note»: la spiegazione data ad Aida finisce nella nota della riga. */
export function useRispostaNelleNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tentativoId: string) =>
      apiFetch<{ ok: boolean; effetto: string }>(
        `/api/v1/m3/domande/${id}/tentativi/${tentativoId}/note`,
        { method: "POST" },
      ),
    onSuccess: () => invalidaDomande(qc),
  });
}

/** Rimette subito fra le aperte una domanda sospesa. */
export function useRiprendiDomanda(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean }>(`/api/v1/m3/domande/${id}/riprendi`, { method: "POST" }),
    onSuccess: () => invalidaDomande(qc),
  });
}

export type PercheNonPertinente =
  | "era_collega"
  | "era_spedizioniere"
  | "era_fornitore"
  | "info_nella_mail"
  | "non_riguarda_spedizioni"
  | "altro";

/** Archivia una domanda senza risposta. NON conta come risposta: `chiusa` e
 *  `risolta` sono due colonne diverse, o la percentuale di risposte di Aida si
 *  gonfierebbe facendo pulizia. Con «non pertinente» e un perché che dice chi
 *  era l'interlocutore, Aida lo impara (30-09). */
export function useChiudiDomanda(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      motivo: "superata" | "non_pertinente";
      perche?: PercheNonPertinente | null;
      nota?: string | null;
    }) =>
      apiFetch<{ ok: boolean; appreso: string[] }>(
        `/api/v1/m3/domande/${id}/chiudi`,
        JSON_POST(body),
      ),
    onSuccess: () => invalidaDomande(qc),
  });
}

const RUBRICA_KEY = ["m3", "rubrica"] as const;

/** Chi è chi: gli indirizzi dichiarati e gli spedizionieri coi loro clienti. */
export function useRubrica(abilitata = true) {
  return useQuery({
    queryKey: RUBRICA_KEY,
    queryFn: () =>
      apiFetch<{ indirizzi: VoceRubrica[]; spedizionieri: SpedizioniereConClienti[] }>(
        "/api/v1/m3/rubrica",
      ),
    enabled: abilitata,
  });
}

/** «Chi è?»: dichiara il ruolo di un indirizzo. Le domande con quell'indirizzo
 *  si ricalcolano da sole, quindi si ricarica tutto ciò che le mostra. */
export function useDichiaraIndirizzo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, ...body }: DichiaraIndirizzo & { email: string }) =>
      apiFetch<{
        effetti: string[];
        ricalcolo: { id: string; azione: string; dopo: string | null; effetti: string[] }[];
      }>(`/api/v1/m3/rubrica/${encodeURIComponent(email)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RUBRICA_KEY });
      invalidaDomande(qc);
    },
  });
}

export function useTogliIndirizzo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<{ ok: boolean }>(`/api/v1/m3/rubrica/${encodeURIComponent(email)}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RUBRICA_KEY });
      invalidaDomande(qc);
    },
  });
}

/** Le mail del thread da cui la domanda è nata, senza firma (solo Logistica). */
export function useConversazioneDomanda(id: string, abilitata: boolean) {
  return useConversazione(`/api/v1/m3/domande/${id}/conversazione`, abilitata);
}

/** La conversazione da una domanda o da una riga: `base` è l'endpoint. */
export function useConversazione(base: string, abilitata = true) {
  return useQuery({
    queryKey: ["m3", "conversazione", base],
    queryFn: () => apiFetch<ConversazioneDomanda>(base),
    enabled: abilitata,
  });
}

/** Una domanda aperta, com'è nella pagina delle domande (null se chiusa). */
export function useDomanda(id: string | null) {
  return useQuery({
    queryKey: [...DOMANDE_KEY, "una", id],
    queryFn: () => apiFetch<DomandaAida | null>(`/api/v1/m3/domande/${id}`),
    enabled: !!id,
  });
}

/** Cosa contiene una commessa, visto dalle Spedizioni (M1 a Marina non è aperto). */
export function useCommessaM3(numero: number | null) {
  return useQuery({
    queryKey: ["m3", "commessa", numero],
    queryFn: () => apiFetch<CommessaM3>(`/api/v1/m3/commesse/${numero}`),
    enabled: numero != null,
    retry: false,
  });
}

/** I clienti dell'anagrafica per nome o codice (per il «Chi è?»). */
export function useCercaClienti(q: string) {
  return useQuery({
    queryKey: ["m3", "clienti-cerca", q],
    queryFn: () =>
      apiFetch<ClienteTrovato[]>(`/api/v1/m3/clienti-cerca?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2,
    staleTime: 60_000,
  });
}

/** Chiude in blocco quelle senza più oggetto (riga cancellata, carico partito). */
/**
 * L'archivio: archiviate e risolte insieme, dalla più recente.
 *
 * Query separata dalle aperte, non un filtro sulla stessa: le due liste hanno
 * cicli di vita diversi (la lista aperte si invalida a ogni risposta, l'archivio
 * quasi mai) e tenerle insieme significherebbe ricaricare 62 righe ferme a ogni
 * gesto su una delle 9 vive.
 */
/**
 * Le righe che portano la stessa NDP: una NDP corretta e rimandata ne creava una
 * nuova a ogni giro, invece di aggiornare quella che quel numero ce l'aveva già.
 *
 * Query a sé e non dentro il tabellone: è una condizione **eccezionale** (16
 * gruppi su 151 righe al 12-09, destinati a zero dopo la fusione), e un carico
 * di lavoro che sparisce non deve pesare su ogni apertura della pagina.
 */
export function useDuplicatiNdp() {
  return useQuery({
    queryKey: ["m3", "carichi-previsti", "duplicati"],
    queryFn: () => apiFetch<DuplicatoNdp[]>("/api/v1/m3/carichi-previsti/duplicati"),
  });
}

/** Unisce le copie di una NDP in una riga sola: stessa logica dello script in blocco. */
export function useFondiDuplicati() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ndpNumero: string) =>
      apiFetch<{ fusi: { tenuta: string; assorbite: string[]; campi_recuperati: string[] }[] }>(
        `/api/v1/m3/carichi-previsti/duplicati/${encodeURIComponent(ndpNumero)}/fondi`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "carichi-previsti", "duplicati"] });
      invalidaCp(qc);
    },
  });
}

/**
 * Le anomalie del tabellone: righe che il gestionale smentisce (partita senza
 * DDT con la NDP ancora aperta, NDP che in Cube non esiste). Le scrive il giro
 * dei 15 minuti: qui si leggono e basta, la pagina non interroga Cube.
 */
export function useAnomalieTabellone() {
  return useQuery({
    queryKey: [...CP_KEY, "anomalie"],
    queryFn: () => apiFetch<AnomaliaTabellone[]>("/api/v1/m3/carichi-previsti/anomalie"),
  });
}

/** Applica la correzione suggerita (dopo la conferma). */
export function useCorreggiAnomalia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ azione: string; riga: string; unita?: { cliente: string } | null }>(
        `/api/v1/m3/carichi-previsti/anomalie/${id}/correggi`,
        { method: "POST" },
      ),
    onSuccess: () => invalidaCp(qc),
  });
}

/** «Non è un'anomalia»: resta chiusa anche se il giro la rivede. */
export function useIgnoraAnomalia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, nota }: { id: string; nota: string }) =>
      apiFetch<void>(`/api/v1/m3/carichi-previsti/anomalie/${id}/ignora`, {
        method: "POST",
        body: JSON.stringify({ nota: nota.trim() || null }),
      }),
    onSuccess: () => invalidaCp(qc),
  });
}

export function useArchivioDomande(abilitata: boolean) {
  return useQuery({
    queryKey: [...DOMANDE_KEY, "archivio"],
    queryFn: () => apiFetch<DomandaArchiviata[]>("/api/v1/m3/domande/archivio"),
    enabled: abilitata,
  });
}

/** Rimette in lista una domanda archiviata per sbaglio. */
export function useRiapriDomanda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean }>(`/api/v1/m3/domande/${id}/riapri`, { method: "POST" }),
    onSuccess: () => invalidaDomande(qc),
  });
}

export function usePulisciDomande() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ chiuse: number }>("/api/v1/m3/domande/pulisci", { method: "POST" }),
    onSuccess: () => invalidaDomande(qc),
  });
}

/**
 * L'anagrafica dei trasportatori, ordinata per chi la sta guardando.
 *
 * `clienteCodice` non filtra: **ordina**. I vettori che quel cliente ha già
 * usato salgono in cima, contati dai carichi passati — è così che l'associazione
 * cliente↔trasportatore resta vera senza che nessuno la mantenga.
 */
export function useTrasportatori(q: string, clienteCodice: number | null) {
  const p = new URLSearchParams();
  if (q.trim()) p.set("q", q.trim());
  if (clienteCodice != null) p.set("cliente_codice", String(clienteCodice));
  return useQuery({
    queryKey: ["m3", "trasportatori", q.trim(), clienteCodice],
    queryFn: () => apiFetch<Trasportatore[]>(`/api/v1/m3/trasportatori?${p.toString()}`),
  });
}

/** Chi ritira questo carico: le tre risposte alla domanda di Aida (è uno che
 *  conosciamo / è nuovo / il nome letto è un alias di uno esistente), più la
 *  quarta implicita — nessuna delle tre, cioè «non era un vettore». */
export function useCpTrasportatore(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      trasportatore_id?: string | null;
      nuovo?: { nome: string; alias?: string[]; email?: string[]; note?: string | null };
      alias_per?: string | null;
      /** Ritira il cliente stesso (30-09-2026). */
      cliente_stesso?: boolean;
    }) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}/trasportatore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaDomande(qc),
  });
}

/**
 * L'uscita dichiarata quando nel portale non c'è nessun documento (migr 0065).
 *
 * Deliberatamente **separata** da `useCpPartenza`: è un'altra azione, con un
 * altro endpoint e altri vincoli, e tenerle nella stessa chiamata con un flag in
 * più avrebbe reso la forzatura un parametro della strada normale — cioè la cosa
 * che stiamo cercando di non far diventare.
 */
export function useCpForzaUscita(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { giorno: string; motivo: string; nota?: string | null }) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}/forza-uscita`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaCp(qc),
  });
}

/**
 * Il calendario dei ritiri: le stesse righe del tabellone, disposte sui giorni.
 *
 * Condivide `CP_KEY` con il tabellone, quindi ogni scrittura (una data, una
 * fascia, un'uscita confermata) aggiorna entrambe le viste. È il motivo per cui
 * non possono divergere: non sono due copie, sono due letture della stessa cosa.
 */
export function useCalendario(da: string | null, giorni = 14) {
  const qs = new URLSearchParams({ giorni: String(giorni) });
  if (da) qs.set("da", da);
  return useQuery({
    queryKey: [...CP_KEY, "calendario", da ?? "oggi", giorni],
    queryFn: () => apiFetch<CalendarioResponse>(`/api/v1/m3/calendario?${qs}`),
    staleTime: 10_000,
  });
}

/** Il tabellone delle partenze di un giorno (default oggi). */
export function usePartenze(giorno?: string) {
  const qs = giorno ? `?giorno=${giorno}` : "";
  return useQuery({
    queryKey: [...CP_KEY, "partenze", giorno ?? "oggi"],
    queryFn: () => apiFetch<PartenzeGiorno>(`/api/v1/m3/partenze${qs}`),
    refetchInterval: 60_000, // la pagina resta aperta: si aggiorna da sola
  });
}

/** Aggancia riferimento cliente → commessa (persiste nel mapping). */
export function useCpAggancia(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AgganciaCommessaBody) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}/aggancia`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaCp(qc),
  });
}

/** Crea un SOLLECITO in Bacheca per Marina. */
/** Sollecito in Bacheca per Marina.
 *
 * ⚠️ Dal 27-08-2026 **nessuno lo chiama dal portale**: il pulsante stava nel
 * drawer del carico, cioè davanti a Marina, e creava un promemoria per lei stessa
 * (togliere chiesto da Enrico). L'endpoint resta valido per gli altri canali —
 * l'assistente di presidenza e Aida, che sollecitano dall'esterno. */
export function useCpSollecito(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ bacheca_voce_id: string }>(
        `/api/v1/m3/carichi-previsti/${id}/sollecito`,
        { method: "POST" },
      ),
    onSuccess: () => invalidaCp(qc),
  });
}

// ── Associazioni email → cliente (tab Impostazioni) ───────────────────

const CLIENTE_EMAIL_KEY = ["m3", "cliente-email"];

export function useM3ClienteEmail() {
  return useQuery({
    queryKey: CLIENTE_EMAIL_KEY,
    queryFn: () => apiFetch<ClienteEmailResponse>("/api/v1/m3/cliente-email"),
    staleTime: 30_000,
  });
}

export function useM3RisolviPropostaEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PropostaResolve }) =>
      apiFetch<ClienteEmailResponse>(`/api/v1/m3/cliente-email/proposte/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLIENTE_EMAIL_KEY }),
  });
}

export function useM3UpsertClienteEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, body }: { email: string; body: ClienteEmailUpsert }) =>
      apiFetch<ClienteEmailResponse>(
        `/api/v1/m3/cliente-email/${encodeURIComponent(email)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLIENTE_EMAIL_KEY }),
  });
}

export function useM3EliminaClienteEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<ClienteEmailResponse>(
        `/api/v1/m3/cliente-email/${encodeURIComponent(email)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLIENTE_EMAIL_KEY }),
  });
}


// ── Commesse pronte: merce pronta per cliente e offerte (03-10-2026) ──────────

const MERCE_PRONTA_KEY = ["m3", "merce-pronta"];

export function useMerceProntaClienti() {
  return useQuery({
    queryKey: [...MERCE_PRONTA_KEY, "clienti"],
    queryFn: () => apiFetch<MerceProntaClienti>("/api/v1/m3/merce-pronta/clienti"),
    staleTime: 60_000,
  });
}

export function useMerceProntaCliente(codice: number | null) {
  return useQuery({
    queryKey: [...MERCE_PRONTA_KEY, "cliente", codice],
    queryFn: () =>
      apiFetch<MerceProntaCliente>(`/api/v1/m3/merce-pronta/clienti/${codice}`),
    enabled: codice != null,
    staleTime: 60_000,
  });
}

export function useMailOffertaProposta(codice: number) {
  return useMutation({
    mutationFn: (body: { righe: string[]; lingua: string }) =>
      apiFetch<MailOffertaProposta>(
        `/api/v1/m3/merce-pronta/clienti/${codice}/mail/proposta`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      ),
  });
}

/** Scarica l'Excel o la bozza di mail: ognuno registra (o aggiorna) un'offerta. */
export function useScaricaOfferta(codice: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      tipo,
      body,
    }: {
      tipo: "excel" | "mail";
      body: Record<string, unknown>;
    }) =>
      apiDownloadPost(
        `/api/v1/m3/merce-pronta/clienti/${codice}/${tipo}`,
        body,
        tipo === "excel" ? "merce disponibile.xlsx" : "merce disponibile.eml",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: MERCE_PRONTA_KEY }),
  });
}
